/**
 * GoalObjectiveTreeSelect - A hierarchical tree selector for Goals and Objectives.
 * Uses InstUI Select for multi-select with visual hierarchy (bold goals, indented objectives).
 *
 * @param {Object} props
 * @param {Array} props.goals - Pre-built goals array: [{ id, description, objectives: [{ id, description }] }]
 * @param {Function} [props.onChange] - Callback: ({ goalIds: string[], objectiveIds: string[] }) => void
 * @param {string} [props.interaction] - "enabled" | "disabled" (default: "enabled")
 * @param {string} [props.label] - Override label (default: i18n key)
 * @param {string} [props.placeholder] - Override placeholder (default: i18n key)
 * @param {string} [props.disabledTooltip] - Tooltip text shown when disabled; callers should provide context-specific text
 */

import { IconCheckLine, Select, Tag, Tooltip } from "@instructure/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NO_OBJECTIVE_ID } from "../../../utils/buildGoalTreeFromMeasures";

/**
 * Classifies selected nodes by _type into goalIds and objectiveIds.
 * Exported for testability in property tests.
 *
 * @param {Array} selectedNodes - Array of { value, _type } objects
 * @returns {{ goalIds: string[], objectiveIds: string[] }}
 */
export function classifySelections(selectedNodes) {
  const goalIds = [];
  const objectiveIds = [];

  for (const node of selectedNodes) {
    if (node._type === "goal") {
      goalIds.push(node.value);
    } else if (node._type === "objective") {
      objectiveIds.push(node.value);
    }
  }

  return { goalIds, objectiveIds };
}

const MAX_VISIBLE_TAGS = 2;

const GoalObjectiveTreeSelect = ({
  goals,
  onChange,
  interaction = "enabled",
  label,
  placeholder,
  disabledTooltip,
}) => {
  const { t } = useTranslation();
  const isDisabled = interaction === "disabled";

  const resolvedLabel =
    label || t("assessmentPlanningDashboard.goalObjectiveTreeSelect.label");
  const resolvedPlaceholder =
    placeholder ||
    t("assessmentPlanningDashboard.goalObjectiveTreeSelect.placeholder");
  const resolvedDisabledTooltip =
    disabledTooltip ||
    t(
      "assessmentPlanningDashboard.goalObjectiveTreeSelect.disabledTooltip_generic"
    );

  // Track previous goals reference and interaction to detect changes for reset
  const prevGoalsRef = useRef(goals);
  const prevInteractionRef = useRef(interaction);

  // Stable ref for onChange to avoid including it in effect deps
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  const [isShowingOptions, setIsShowingOptions] = useState(false);
  const [highlightedOptionId, setHighlightedOptionId] = useState(null);
  const [selectedGoalIds, setSelectedGoalIds] = useState([]);
  const [selectedObjectiveIds, setSelectedObjectiveIds] = useState([]);

  // Build flat options list from goals
  const options = useMemo(() => {
    const noObjectiveLabel = t(
      "assessmentPlanningDashboard.goalObjectiveTreeSelect.noObjective"
    );
    const opts = [];
    (goals || []).forEach((goal) => {
      const goalId = String(goal.id);
      opts.push({
        id: `goal-${goalId}`,
        value: goalId,
        label: goal.description || goal.id,
        type: "goal",
      });
      (goal.objectives || []).forEach((obj) => {
        const objId = String(obj.id);
        const isNoObjective = objId.endsWith(NO_OBJECTIVE_ID);
        opts.push({
          id: `obj-${objId}`,
          value: objId,
          label: isNoObjective ? noObjectiveLabel : obj.description || obj.id,
          type: "objective",
          goalId,
        });
      });
    });
    return opts;
  }, [goals, t]);

  // Reset when goals reference changes or interaction transitions to "disabled"
  useEffect(() => {
    const goalsChanged = prevGoalsRef.current !== goals;
    const becameDisabled =
      prevInteractionRef.current !== "disabled" && interaction === "disabled";

    if (goalsChanged || becameDisabled) {
      setSelectedGoalIds([]);
      setSelectedObjectiveIds([]);

      if (onChangeRef.current) {
        onChangeRef.current({ goalIds: [], objectiveIds: [] });
      }
    }

    prevGoalsRef.current = goals;
    prevInteractionRef.current = interaction;
  }, [goals, interaction]);

  // Helper: check if a goal is selected
  const isGoalSelected = useCallback(
    (goalId) => selectedGoalIds.includes(String(goalId)),
    [selectedGoalIds]
  );

  // Helper: check if an objective is selected
  const isObjectiveSelected = useCallback(
    (objId) => selectedObjectiveIds.includes(String(objId)),
    [selectedObjectiveIds]
  );

  // Get all objective IDs for a goal
  const getObjectiveIdsForGoal = useCallback(
    (goalId) => {
      const goal = (goals || []).find((g) => String(g.id) === String(goalId));
      if (!goal || !goal.objectives) return [];
      return goal.objectives.map((o) => String(o.id));
    },
    [goals]
  );

  // Fire onChange callback
  const fireOnChange = useCallback((newGoalIds, newObjectiveIds) => {
    if (onChangeRef.current) {
      onChangeRef.current({
        goalIds: newGoalIds,
        objectiveIds: newObjectiveIds,
      });
    }
  }, []);

  // Handle selecting/deselecting an option
  const handleSelectOption = useCallback(
    (_event, { id }) => {
      if (!id) return;

      if (id.startsWith("goal-")) {
        const goalId = id.replace("goal-", "");
        const objectiveIds = getObjectiveIdsForGoal(goalId);

        if (isGoalSelected(goalId)) {
          // Deselect goal and all its objectives
          const newGoalIds = selectedGoalIds.filter((gid) => gid !== goalId);
          const newObjectiveIds = selectedObjectiveIds.filter(
            (oid) => !objectiveIds.includes(oid)
          );
          setSelectedGoalIds(newGoalIds);
          setSelectedObjectiveIds(newObjectiveIds);
          fireOnChange(newGoalIds, newObjectiveIds);
        } else {
          // Select goal and all its objectives
          const newGoalIds = [...selectedGoalIds, goalId];
          const newObjectiveIds = [
            ...selectedObjectiveIds.filter(
              (oid) => !objectiveIds.includes(oid)
            ),
            ...objectiveIds,
          ];
          setSelectedGoalIds(newGoalIds);
          setSelectedObjectiveIds(newObjectiveIds);
          fireOnChange(newGoalIds, newObjectiveIds);
        }
      } else if (id.startsWith("obj-")) {
        const objId = id.replace("obj-", "");
        // Find parent goal
        const parentOpt = options.find(
          (o) => o.type === "objective" && o.value === objId
        );
        const parentGoalId = parentOpt?.goalId;

        if (isObjectiveSelected(objId)) {
          // Deselect objective
          const newObjectiveIds = selectedObjectiveIds.filter(
            (oid) => oid !== objId
          );
          // If no other objectives of this goal remain selected, deselect the goal too
          let newGoalIds = selectedGoalIds;
          if (parentGoalId && isGoalSelected(parentGoalId)) {
            const siblingObjIds = getObjectiveIdsForGoal(parentGoalId);
            const anyRemaining = siblingObjIds.some(
              (oid) => oid !== objId && newObjectiveIds.includes(oid)
            );
            if (!anyRemaining) {
              newGoalIds = selectedGoalIds.filter(
                (gid) => gid !== parentGoalId
              );
            }
          }
          setSelectedGoalIds(newGoalIds);
          setSelectedObjectiveIds(newObjectiveIds);
          fireOnChange(newGoalIds, newObjectiveIds);
        } else {
          // Select objective and its parent goal (but not sibling objectives)
          const newObjectiveIds = [...selectedObjectiveIds, objId];
          let newGoalIds = selectedGoalIds;
          if (parentGoalId && !isGoalSelected(parentGoalId)) {
            newGoalIds = [...selectedGoalIds, parentGoalId];
          }
          setSelectedGoalIds(newGoalIds);
          setSelectedObjectiveIds(newObjectiveIds);
          fireOnChange(newGoalIds, newObjectiveIds);
        }
      }
    },
    [
      selectedGoalIds,
      selectedObjectiveIds,
      options,
      isGoalSelected,
      isObjectiveSelected,
      getObjectiveIdsForGoal,
      fireOnChange,
    ]
  );

  // Handle removing a tag
  const handleRemoveTag = useCallback(
    (tagType, tagValue) => {
      if (tagType === "goal") {
        // Remove goal and all its objectives
        const objectiveIds = getObjectiveIdsForGoal(tagValue);
        const newGoalIds = selectedGoalIds.filter((gid) => gid !== tagValue);
        const newObjectiveIds = selectedObjectiveIds.filter(
          (oid) => !objectiveIds.includes(oid)
        );
        setSelectedGoalIds(newGoalIds);
        setSelectedObjectiveIds(newObjectiveIds);
        fireOnChange(newGoalIds, newObjectiveIds);
      } else {
        // Remove objective only — parent goal remains selected
        const newObjectiveIds = selectedObjectiveIds.filter(
          (oid) => oid !== tagValue
        );
        setSelectedObjectiveIds(newObjectiveIds);
        fireOnChange(selectedGoalIds, newObjectiveIds);
      }
    },
    [
      selectedGoalIds,
      selectedObjectiveIds,
      getObjectiveIdsForGoal,
      fireOnChange,
    ]
  );

  // Build tags for display
  const tags = useMemo(() => {
    const result = [];
    selectedGoalIds.forEach((goalId) => {
      const goal = (goals || []).find((g) => String(g.id) === goalId);
      if (goal) {
        result.push({
          id: `goal-${goalId}`,
          label: goal.description || goal.id,
          type: "goal",
          value: goalId,
        });
      }
    });
    selectedObjectiveIds.forEach((objId) => {
      // Only show objective tag if its parent goal is NOT selected
      const parentGoal = (goals || []).find((g) =>
        g.objectives?.some((o) => String(o.id) === objId)
      );
      if (parentGoal && !selectedGoalIds.includes(String(parentGoal.id))) {
        const obj = parentGoal.objectives.find((o) => String(o.id) === objId);
        if (obj) {
          result.push({
            id: `obj-${objId}`,
            label: obj.description || obj.id,
            type: "objective",
            value: objId,
          });
        }
      }
    });
    return result;
  }, [goals, selectedGoalIds, selectedObjectiveIds]);

  // Render tags in the input area
  const renderTags = () => {
    if (tags.length === 0) return null;

    const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
    const remainingCount = tags.length - MAX_VISIBLE_TAGS;

    return (
      <span>
        {visibleTags.map((tag) => (
          <Tag
            key={tag.id}
            text={
              tag.label.length > 20
                ? `${tag.label.substring(0, 20)}...`
                : tag.label
            }
            dismissible
            margin="0 xx-small 0 0"
            onClick={() => handleRemoveTag(tag.type, tag.value)}
          />
        ))}
        {remainingCount > 0 && (
          <Tag
            key="more"
            text={t(
              "assessmentPlanningDashboard.goalObjectiveTreeSelect.moreCount",
              { count: remainingCount }
            )}
            margin="0 xx-small 0 0"
          />
        )}
      </span>
    );
  };

  // Display text when dropdown is closed — always empty since tags communicate selections
  const getDisplayText = () => {
    return "";
  };

  // Handle highlight navigation
  const handleHighlightOption = useCallback(
    (_event, { id, direction }) => {
      if (id) {
        setHighlightedOptionId(id);
      } else if (direction) {
        // Let InstUI handle directional navigation
        setHighlightedOptionId((prevId) => {
          // Find current index and move
          const currentIdx = options.findIndex((o) => o.id === prevId);
          if (direction === 1) {
            const nextIdx =
              currentIdx < options.length - 1 ? currentIdx + 1 : 0;
            return options[nextIdx]?.id || null;
          } else {
            const prevIdx =
              currentIdx > 0 ? currentIdx - 1 : options.length - 1;
            return options[prevIdx]?.id || null;
          }
        });
      }
    },
    [options]
  );

  const selectContent = (
    <Select
      renderLabel={resolvedLabel}
      placeholder={tags.length === 0 ? resolvedPlaceholder : ""}
      interaction={interaction}
      isShowingOptions={isShowingOptions}
      onRequestShowOptions={() => setIsShowingOptions(true)}
      onRequestHideOptions={() => setIsShowingOptions(false)}
      onRequestHighlightOption={handleHighlightOption}
      onRequestSelectOption={handleSelectOption}
      inputValue={isShowingOptions ? "" : getDisplayText()}
      renderBeforeInput={tags.length > 0 ? renderTags() : null}
      assistiveText={t(
        "assessmentPlanningDashboard.goalObjectiveTreeSelect.assistiveText"
      )}
    >
      {options.map((opt) => {
        const isSelected =
          opt.type === "goal"
            ? isGoalSelected(opt.value)
            : isObjectiveSelected(opt.value);

        return (
          <Select.Option
            key={opt.id}
            id={opt.id}
            isHighlighted={highlightedOptionId === opt.id}
          >
            <span
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "0.5rem",
                paddingLeft: opt.type === "objective" ? "1.5rem" : "0",
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  paddingTop: "0.125rem",
                  width: "1.25em",
                  height: "1.25em",
                }}
              >
                {isSelected && (
                  <IconCheckLine
                    size="x-small"
                    color={
                      highlightedOptionId === opt.id
                        ? "primary-inverse"
                        : "success"
                    }
                  />
                )}
              </span>
              <span>
                {opt.type === "goal" ? <strong>{opt.label}</strong> : opt.label}
              </span>
            </span>
          </Select.Option>
        );
      })}
    </Select>
  );

  if (isDisabled) {
    return (
      <Tooltip renderTip={resolvedDisabledTooltip} placement="top">
        <span>{selectContent}</span>
      </Tooltip>
    );
  }

  return selectContent;
};

export default GoalObjectiveTreeSelect;
